import { httpServices } from './client';
import { normalizeFolderListing } from './normalize';
import { getFolder, getFolderDescendantsBatch } from './folders';

/**
 * The v2 document library endpoints (rakuyaku-app-api docs/v2/documents-api.md):
 * listings with the v2 fields, name search, lookup by id, delete previews, recoverable
 * deletion and restore, retry, original download, contents CSV and uploads
 * with per-file outcomes.
 */

/** A failed call: the HTTP client's error shape plus the API's stable code. */
export interface ApiFailure {
  success: false;
  /** 0 when no response arrived (network failure). */
  status: number;
  error?: string;
  code?: string;
  body?: Record<string, unknown>;
}

export type ApiResult<T> = { success: true; data: T } | ApiFailure;

export interface LibraryFolder {
  id: string | number;
  name: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string | number | null;
  updated_by?: string | number | null;
  parent_id?: string | number | null;
  default?: boolean;
  standard?: boolean;
  read_only?: boolean;
}

export interface LibraryFile {
  id: string | number;
  name: string;
  type?: string;
  status?: string;
  /** Presigned URL of the processed object (the converted PDF for DOCX). */
  path?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string | number | null;
  updated_by?: string | number | null;
  default?: boolean;
  processed?: boolean;
  sha256?: string | null;
  size_bytes?: number | null;
  usable?: boolean;
  unusable_reason?: string | null;
  unusable_message?: string | null;
  read_only?: boolean;
}

export interface FolderListing {
  folders: LibraryFolder[];
  files: LibraryFile[];
}

export interface SearchHit {
  type: 'folder' | 'file';
  id: number | string;
  name: string;
  parent_id: number | string | null;
  path: string;
  parent_path: string;
  standard: boolean;
  file_type: string | null;
  status: string | null;
  size_bytes: number | null;
  updated_at: string | null;
  /** As in the folder listing: standard, and this user may not change it. */
  read_only?: boolean;
  /** The creator's display name; null when the user no longer exists. */
  created_by?: string | null;
  created_by_id?: number | string | null;
}

export interface SearchPage {
  hits: SearchHit[];
  found: number;
  page: number;
  lastPage: number;
  perPage: number;
}

export type SearchScope = 'all' | 'standard' | 'organization';

/** A folder above a located item. */
export interface LocatedPathEntry {
  id: number | string;
  name: string;
}

export interface LocatedFolderEntry {
  id: number | string;
  name: string;
  parent_id: number | string | null;
  /** From the top level down to the parent folder; [] at the top level. */
  path: LocatedPathEntry[];
  standard: boolean;
  read_only: boolean;
}

export interface LocatedDocumentEntry {
  id: number | string;
  name: string;
  folder_id: number | string | null;
  /** From the top level down to the document's own folder. */
  path: LocatedPathEntry[];
  standard: boolean;
  read_only: boolean;
  file_type: string | null;
  status: string | null;
}

export interface LocateResult {
  folders: LocatedFolderEntry[];
  documents: LocatedDocumentEntry[];
  /** Gone for this user: deleted, another organization's, or unknown. */
  missingFolderIds: string[];
  missingDocumentIds: string[];
}

/** Ids one GET /llm/library/locate accepts at most (documents.max_bulk_ids). */
export const LOCATE_MAX_IDS = 500;

export interface DeletePreview {
  folder?: { id: number; name: string };
  can_delete: boolean;
  folder_count: number;
  file_count: number;
  inquiry_count: number;
  inquiries: {
    id: number;
    name: string;
    owner_id?: number;
    is_own?: boolean;
    selected_folder_count?: number;
    selected_file_count?: number;
  }[];
  other_organization_inquiry_count: number;
}

export interface Deletion {
  deletion_id: string;
  purge_after: string;
  undo_seconds: number;
  folder_count: number;
  file_count: number;
  inquiry_count: number;
}

export interface Restored {
  deletion_id: string;
  folder_count: number;
  file_count: number;
  inquiry_count: number;
}

export type UploadOutcomeStatus = 'accepted' | 'skipped' | 'rejected';

export interface UploadOutcome {
  index: number;
  name: string;
  relative_path: string | null;
  status: UploadOutcomeStatus;
  reason: string | null;
  message: string | null;
  file_id: number | null;
  sha256?: string | null;
  size_bytes?: number | null;
}

export interface UploadResponse {
  success: boolean;
  data?: LibraryFile[];
  results?: UploadOutcome[];
  summary?: { accepted: number; skipped: number; rejected: number };
  code?: string;
  message?: string;
  error?: string;
  status?: number;
}

export type OnDuplicate = 'separate' | 'skip';

export interface SelectionTotalsFile {
  id: number;
  name: string;
  source?: 'library' | 'attachment';
  size_bytes: number | null;
  status: string;
  usable: boolean;
  unusable_reason: string | null;
  exclusion_reason?: string | null;
  duplicate_of: number | null;
}

/** `selection_totals` on GET /llm/chat/inquiry/{id} (chat-runs-api.md §3.6). */
export interface SelectionTotals {
  file_count: number;
  total_size_mb: number;
  total_bytes: number;
  duplicate_count: number;
  excluded_count: number;
  pending_count?: number;
  ready_count?: number;
  files: SelectionTotalsFile[];
  excluded?: { id: number; name: string | null; reason: string }[];
}

const llm = () => httpServices.llmService;

const failure = (response: unknown): ApiFailure => {
  const value = (response ?? {}) as Partial<ApiFailure>;
  return {
    success: false,
    status: typeof value.status === 'number' ? value.status : 0,
    error: value.error,
    code: value.code,
    body: value.body,
  };
};

const unwrap = <T>(response: unknown): ApiResult<T> => {
  const value = response as { success?: boolean; data?: T } | undefined;
  return value?.success
    ? { success: true, data: value.data as T }
    : failure(response);
};

/** GET /llm/folder[?folder_id=]; null means the top level. */
export const listFolder = async (
  folderId: string | null,
): Promise<ApiResult<FolderListing>> => {
  const response = await getFolder(folderId ? { folder_id: folderId } : {});
  if (!response?.success) return failure(response);
  return {
    success: true,
    data: normalizeFolderListing(response) as unknown as FolderListing,
  };
};

/** POST /llm/folder/descendants for up to 100 roots. */
export const folderDescendants = async (
  folderIds: string[],
): Promise<
  ApiResult<Record<string, { folder_ids: unknown[]; file_ids: unknown[] }>>
> => {
  const response = await getFolderDescendantsBatch(folderIds);
  if (!response?.success) return failure(response);
  return {
    success: true,
    data: (response.data?.descendants ?? {}) as Record<
      string,
      { folder_ids: unknown[]; file_ids: unknown[] }
    >,
  };
};

/**
 * GET /llm/search: case-insensitive partial match on names. `folderId`
 * limits it to what lies below that folder, at any depth (resolved by the
 * server; 404 `folder_not_found` when the folder is gone).
 */
export const searchLibrary = async (params: {
  q: string;
  type?: 'all' | 'folder' | 'file';
  scope?: SearchScope;
  folderId?: string | null;
  page?: number;
  perPage?: number;
}): Promise<ApiResult<SearchPage>> => {
  const response = await llm().get<{
    success: boolean;
    data: SearchHit[];
    found: number;
    current_page: number;
    last_page: number;
    per_page: number;
  }>('/search', {
    q: params.q,
    type: params.type ?? 'all',
    scope: params.scope ?? 'all',
    ...(params.folderId ? { folder_id: params.folderId } : {}),
    page: params.page ?? 1,
    per_page: params.perPage ?? 100,
  });
  if (!response?.success) return failure(response);
  return {
    success: true,
    data: {
      hits: response.data ?? [],
      found: Number(response.found ?? 0),
      page: Number(response.current_page ?? 1),
      lastPage: Number(response.last_page ?? 1),
      perPage: Number(response.per_page ?? params.perPage ?? 100),
    },
  };
};

const idList = (values: unknown): string[] =>
  Array.isArray(values) ? values.map(String) : [];

/**
 * GET /llm/library/locate: folders and documents by id, with the folders
 * above them. At most LOCATE_MAX_IDS ids in all; a failed call is a network
 * or server failure, never a missing item.
 */
export const locateLibraryItems = async (params: {
  folderIds?: readonly string[];
  documentIds?: readonly string[];
}): Promise<ApiResult<LocateResult>> => {
  const folderIds = params.folderIds ?? [];
  const documentIds = params.documentIds ?? [];
  const response = await llm().get<{
    success: boolean;
    data?: {
      folders?: LocatedFolderEntry[];
      documents?: LocatedDocumentEntry[];
      missing_folder_ids?: unknown[];
      missing_document_ids?: unknown[];
    };
  }>('/library/locate', {
    ...(folderIds.length ? { folder_ids: folderIds.join(',') } : {}),
    ...(documentIds.length ? { document_ids: documentIds.join(',') } : {}),
  });
  if (!response?.success) return failure(response);
  return {
    success: true,
    data: {
      folders: response.data?.folders ?? [],
      documents: response.data?.documents ?? [],
      missingFolderIds: idList(response.data?.missing_folder_ids),
      missingDocumentIds: idList(response.data?.missing_document_ids),
    },
  };
};

/** GET /llm/folder/{id}/delete-preview */
export const folderDeletePreview = async (folderId: string) =>
  unwrap<DeletePreview>(await llm().get(`/folder/${folderId}/delete-preview`));

/** GET /llm/document/delete-preview?ids= */
export const documentsDeletePreview = async (ids: string[]) =>
  unwrap<DeletePreview>(
    await llm().get('/document/delete-preview', { ids: ids.join(',') }),
  );

/** DELETE /llm/folder with `{ folder_id }` */
export const deleteLibraryFolder = async (folderId: string) =>
  unwrap<Deletion>(
    await llm().delete(
      '/folder',
      { folder_id: folderId },
      { folder_id: folderId },
    ),
  );

/** DELETE /llm/document/{id} */
export const deleteLibraryDocument = async (documentId: string) =>
  unwrap<Deletion>(
    await llm().delete(`/document/${documentId}`, { document_id: documentId }),
  );

/** POST /llm/document/bulk-delete: all or nothing, one deletion. */
export const bulkDeleteDocuments = async (documentIds: string[]) =>
  unwrap<Deletion>(
    await llm().post('/document/bulk-delete', {
      document_ids: documentIds.map(Number),
    }),
  );

/** POST /llm/deletions/{id}/restore */
export const restoreDeletion = async (deletionId: string) =>
  unwrap<Restored>(await llm().post(`/deletions/${deletionId}/restore`, {}));

/** POST /llm/document/{id}/retry */
export const retryDocument = async (documentId: string) =>
  unwrap<{ id: number; status: string }>(
    await llm().post(`/document/${documentId}/retry`, {}),
  );

export interface DownloadedFile {
  blob: Blob;
  filename: string;
  /** The response's Content-Type, with its charset for text. */
  contentType: string;
}

/**
 * Text of a downloaded document in the charset the server declares
 * (Shift_JIS and CP932 uploads come back as uploaded, documents-api §9).
 */
export const decodeText = async (blob: Blob, contentType: string) => {
  const charset = /charset=([^;]+)/i.exec(contentType)?.[1]?.trim() || 'utf-8';
  const bytes = await blob.arrayBuffer();
  try {
    return new TextDecoder(charset.replace(/^"|"$/g, '')).decode(bytes);
  } catch {
    // A label the browser does not know.
    return new TextDecoder('utf-8').decode(bytes);
  }
};

/** `filename*=utf-8''…` wins over `filename="…"`. */
export const filenameFromDisposition = (
  header: string | undefined | null,
  fallback: string,
): string => {
  if (!header) return fallback;
  const extended = /filename\*\s*=\s*(?:utf-8|UTF-8)''([^;]+)/.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain ? plain[1].trim() : fallback;
};

const blobError = async (error: any): Promise<ApiFailure> => {
  const status = error?.response?.status ?? 0;
  let code: string | undefined;
  let message: string | undefined = error?.message;
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      code = json.code;
      message = json.message || json.error || message;
    } catch {
      // not JSON
    }
  }
  return { success: false, status, code, error: message };
};

/** A GET answered with a file (blob), its name from Content-Disposition. */
export const getBlob = async (
  url: string,
  params: Record<string, string> | undefined,
  fallbackName: string,
): Promise<ApiResult<DownloadedFile>> => {
  try {
    const response = await llm().axiosInstance.get(url, {
      params,
      responseType: 'blob',
    });
    return {
      success: true,
      data: {
        blob: response.data,
        filename: filenameFromDisposition(
          response.headers?.['content-disposition'],
          fallbackName,
        ),
        contentType: String(
          response.headers?.['content-type'] ?? response.data?.type ?? '',
        ),
      },
    };
  } catch (error) {
    return blobError(error);
  }
};

/** GET /llm/document/{id}/download: the uploaded original. */
export const downloadOriginal = (documentId: string, name: string) =>
  getBlob(`/document/${documentId}/download`, undefined, name);

/** GET /llm/folder/contents.csv[?folder_id=]: direct children, UTF-8 with BOM. */
export const downloadFolderContentsCsv = (
  folderId: string | null,
  name: string,
) =>
  getBlob(
    '/folder/contents.csv',
    folderId ? { folder_id: folderId } : undefined,
    `folder-contents-${name}.csv`,
  );

/**
 * POST /llm/document/multiple, one request. `parent_id` empty means the top
 * level; `filesPath[]` keeps a folder upload's hierarchy below it.
 */
export const uploadLibraryChunk = async (params: {
  parentId: string | null;
  items: { file: File; path: string }[];
  onDuplicate: OnDuplicate;
}): Promise<UploadResponse> => {
  const form = new FormData();
  params.items.forEach(item => {
    form.append('files[]', item.file);
    form.append('filesPath[]', item.path);
  });
  form.append('on_duplicate', params.onDuplicate);
  if (params.parentId) form.append('parent_id', params.parentId);
  const response = await llm().post<UploadResponse>(
    '/document/multiple',
    form,
    params.parentId ? { parent_id: params.parentId } : {},
  );
  // A 422 for an all-rejected batch still carries the per-file results.
  const body = (response as { body?: UploadResponse }).body;
  return body?.results ? { ...body, success: false } : response;
};

/** The selection totals of a Q&A, from GET /llm/chat/inquiry/{id}. */
export const getSelectionTotals = async (inquiryId: string) => {
  const response = await llm().get<{
    success: boolean;
    data?: {
      selection_totals?: SelectionTotals;
      selection_version?: number;
      folders?: unknown[];
      selection_file_ids?: unknown[];
    };
  }>(`/chat/inquiry/${inquiryId}`);
  return unwrap<{
    selection_totals?: SelectionTotals;
    selection_version?: number;
    folders?: unknown[];
    selection_file_ids?: unknown[];
  }>(response);
};
